import CircleIcon from '@mui/icons-material/RadioButtonUnchecked';
import CirclePlus from '@apps-common/ui-icons/CirclePlus';
import MeasureArea from '@apps-common/ui-icons/Area';
import MeasureIcon from '@apps-common/ui-icons/Measure';
import PolygonPlus from '@apps-common/ui-icons/PolygonPlus';
import PropTypes from 'prop-types';
import React, {Suspense, useEffect, useRef, useState} from 'react';
import SelectAOIIcon from '@apps-common/ui-icons/SelectAoi';
import SquareIcon from '@mui/icons-material/CropDin';
import SquarePlus from '@apps-common/ui-icons/SquarePlus';
import UploadIcon from '@apps-common/ui-icons/Upload';
import {Box} from '@mui/material';
import {KEY_ESCAPE} from 'keycode-js';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';

import MapPopper from '../map-popper.jsx';
import MapPopperButton from '../map-popper-button.jsx';
import useOnKeyDown from '@explorer/hooks/useOnKeyDown';
import {FeatureItem} from './feature.jsx';
import {MAP_DRAW_LAYERS, MAP_TOOLS, MODALS} from '@explorer/constants';
import {aoiTooltipMap} from './tooltips.js';
import {fitExtent, setDrawTool} from '@explorer/actions/map';
import {showModal} from '@explorer/actions/ui';
import {trackEvent} from '@explorer/analytics';

const DownloadAoiDialog = React.lazy(
  () => import('@explorer/components/dialogs/download-aoi.jsx')
);

const sortFeatures = (a, b) => {
  return a.id < b.id ? 1 : -1;
  // return a.getId() < b.getId() ? 1 : -1;
};

const drawTools = [
  {
    name: MAP_TOOLS.DRAW_LOCKED_CIRCLE,
    dataQe: 'draw-circle-locked',
    icon: <CircleIcon />,
  },
  {
    name: MAP_TOOLS.DRAW_LOCKED_RECT,
    dataQe: 'draw-rectangle-locked',
    icon: <SquareIcon />,
  },
  {
    name: MAP_TOOLS.DRAW_POLYGON,
    dataQe: 'draw-polygon',
    icon: {
      [MAP_DRAW_LAYERS.MEASURE]: <MeasureArea />,
      default: <PolygonPlus />,
    },
  },
  {
    name: MAP_TOOLS.DRAW_CIRCLE,
    dataQe: 'draw-circle',
    icon: <CirclePlus />,
  },
  {
    name: MAP_TOOLS.DRAW_RECT,
    dataQe: 'draw-rectangle',
    icon: <SquarePlus />,
  },
  {
    name: MAP_TOOLS.DRAW_LINE,
    dataQe: 'draw-line',
    icon: <MeasureIcon />,
  },
];

const DrawTools = ({
  drawTool = {},
  drawLayer,
  setDrawTool,
  showModal,
  fitExtent,
  open,
  anchorEl,
  features,
  title,
  tempGeometry,
  setTempGeometry,
  toolsAvailable = [
    MAP_TOOLS.DRAW_LOCKED_CIRCLE,
    MAP_TOOLS.DRAW_LOCKED_RECT,
    MAP_TOOLS.DRAW_POLYGON,
    MAP_TOOLS.DRAW_CIRCLE,
    MAP_TOOLS.DRAW_RECT,
  ],
  onDelete,
  uploadAvailable,
  tooltips = aoiTooltipMap,
  canToggleVisibility = true,
  onChangeVisibility,
  onHover = () => {},
  onSave,
}) => {
  const [downloadFeature, setDownloadFeature] = useState(null);
  const listRef = useRef();
  const {t} = useTranslation();

  useEffect(() => {
    // ensure the draw tool is fresh when this is opened.
    if (open) {
      setDrawTool({
        name: MAP_TOOLS.NONE,
        layer: drawLayer,
        target: '',
      });
    }

    return () => {
      // reset on close as well.
      setDrawTool({
        name: MAP_TOOLS.NONE,
        layer: MAP_DRAW_LAYERS.NONE,
        target: '',
      });
    };
  }, [setDrawTool, drawLayer, open]);

  const onToolClick = (toolName, targetId = '') => {
    const active =
      toolName !== drawTool.name ||
      (toolName === MAP_TOOLS.EDIT && drawTool.target !== targetId);

    // this needs cleared before starting to prevent
    //  showing the artifact of the previous feature.
    setTempGeometry(null);

    if (active) {
      setDrawTool({
        name: toolName,
        target: targetId,
        layer: drawLayer,
      });
    } else {
      setDrawTool({
        name: MAP_TOOLS.NONE,
        target: '',
        layer: MAP_DRAW_LAYERS.NONE,
      });
    }
    trackEvent('Draw tool clicked', {
      drawTool: toolName,
      drawLayer,
    });
  };

  const onKeyDown = () => {
    setDrawTool({
      name: MAP_TOOLS.NONE,
      layer: MAP_DRAW_LAYERS.NONE,
      target: '',
    });
    trackEvent('Escape used to end interaction', {
      drawTool: drawTool.name,
      drawLayer,
    });
  };
  useOnKeyDown(KEY_ESCAPE, onKeyDown);

  const onUploadClick = () => {
    showModal(MODALS.UPLOAD);
    trackEvent('Upload button clicked');
  };

  const defaultTitle = t('drawTools.titleAoi');

  return (
    <MapPopper
      title={title || defaultTitle}
      open={open}
      anchorEl={anchorEl}
      placement="left-end"
      ariaLabel="drawTools"
      icon={<SelectAOIIcon />}
      data-qe="draw-tools"
      sx={{width: 330}}
    >
      {downloadFeature && (
        <Suspense>
          <DownloadAoiDialog
            open
            aoi={downloadFeature}
            onClose={() => setDownloadFeature(null)}
            title={t('drawTools.titleDownloadFeature')}
            subtitle={t('drawTools.subtitleDownloadFeature')}
          />
        </Suspense>
      )}
      <Box ref={listRef} sx={{maxHeight: 300, overflow: 'auto'}}>
        {!!tempGeometry &&
          drawTool.name !== MAP_TOOLS.NONE &&
          drawTool.name !== MAP_TOOLS.EDIT && (
            <FeatureItem
              key="temp"
              tempGeometry={tempGeometry}
              tooltips={tooltips}
              isEditing
            />
          )}

        {features
          .filter(feature => !!feature)
          .sort(sortFeatures)
          .map(feature => (
            <FeatureItem
              tooltips={tooltips}
              key={feature.id}
              canToggleVisibility={canToggleVisibility}
              onHover={onHover}
              onDelete={() => {
                onDelete(feature);
                // reset the map tools
                setDrawTool({
                  name: MAP_TOOLS.NONE,
                  layer: MAP_DRAW_LAYERS.NONE,
                  target: '',
                });
                trackEvent('Feature deleted', {
                  drawLayer,
                });
              }}
              onDownload={() => {
                setDownloadFeature({...feature});
                trackEvent('Feature downloaded', {
                  drawLayer,
                });
              }}
              feature={feature}
              tempGeometry={tempGeometry}
              geometryId={feature.id}
              isEditing={
                drawTool.target === feature.id &&
                drawTool.name === MAP_TOOLS.EDIT
              }
              onEdit={() => {
                onToolClick(MAP_TOOLS.EDIT, feature.id);
                trackEvent('Feature edited', {
                  drawLayer,
                });
              }}
              onChangeVisibility={() => {
                onChangeVisibility(feature);
              }}
              onSave={() => {
                onSave(feature);
                setDrawTool({
                  name: MAP_TOOLS.NONE,
                  layer: MAP_DRAW_LAYERS.NONE,
                  target: '',
                });
              }}
              onZoomTo={extent => {
                fitExtent(extent);
                trackEvent('Zoom to feature from draw tools', {
                  drawLayer,
                });
              }}
            />
          ))}
      </Box>
      {uploadAvailable && (
        <MapPopperButton
          key="uploadTool"
          tooltip={t('drawTools.tooltipImport')}
          tooltipPosition="top-end"
          onClick={onUploadClick}
          data-qe="import-tool"
        >
          <UploadIcon />
        </MapPopperButton>
      )}

      {drawTools
        .filter(tool => toolsAvailable.includes(tool.name))
        .map(tool => (
          <MapPopperButton
            key={tool.name}
            tooltip={t(tooltips[tool.name])}
            tooltipPosition="top-end"
            color={
              drawTool.name === tool.name && drawTool.layer === drawLayer
                ? 'primary'
                : undefined
            }
            onClick={() => onToolClick(tool.name)}
            data-qe={tool.dataQe}
          >
            {/* this cascades a default icon. */}
            {tool.icon[drawLayer] || tool.icon.default || tool.icon}
          </MapPopperButton>
        ))}
    </MapPopper>
  );
};

DrawTools.propTypes = {
  drawTool: PropTypes.object,
  setDrawTool: PropTypes.func,
  showModal: PropTypes.func,
  open: PropTypes.bool,
  anchorEl: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.instanceOf(Element),
  ]),
  drawLayer: PropTypes.string,
  title: PropTypes.string,
  tempGeometry: PropTypes.object,
  canToggleVisibility: PropTypes.bool,
  tooltips: PropTypes.object,
  onDelete: PropTypes.func,
  toolsAvailable: PropTypes.array,
  uploadAvailable: PropTypes.bool,
  fitExtent: PropTypes.func,
  onChangeVisibility: PropTypes.func,
  onHover: PropTypes.func,
  onSave: PropTypes.func,
  features: PropTypes.array,
  setTempGeometry: PropTypes.func,
};

const mapStateToProps = state => ({
  drawTool: state.map.drawTool,
});

const mapDispatchProps = {
  setDrawTool,
  showModal,
  fitExtent,
};

export default connect(mapStateToProps, mapDispatchProps)(DrawTools);
